import {
  DocumentDownloadIcon,
  DocumentTextIcon,
  PaperClipIcon,
  PencilIcon,
} from '@heroicons/react/solid';
import { PDFDownloadLink } from '@react-pdf/renderer';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  updateAdminEventReimbursement,
  updateReimbursement,
} from '../../../api/reimbursement';
import BaseModal from '../../../components/Modals/BaseModal';
import AdminEventReimbursementFormModal from '../../../components/Modals/ReimbursmentFormModal/AdminEventReimbursementFormModal';
import { prepareReimbursementFormData } from '../../../components/Modals/ReimbursmentFormModal/utils';
import Checkbox from '../../../components/Form/Checkbox';
import SpeakerReimbursementPDF from '../../../components/PDFExport/SpeakerReimbursementPDF';
import { useAuth } from '../../../contexts/Auth';
import {
  getReimbursmentSubmissionDate,
  EventReimbursementModule,
  Reimbursement,
  ReimbursementAttributes,
  ReimbursementStatus,
} from '../../../interfaces/reimbursment';
import { formatCurrency } from '../../../libs/utils/formatters/currency';
import { formatDate } from '../../../libs/utils/formatters/datetimeFormatter';

interface SpeakerReimbursementRowProps {
  reimbursement: Reimbursement;
  searchWords: string[];
  selected: boolean;
  onSelect: (selected: boolean) => void;
}

const SpeakerReimbursementRow: React.FC<SpeakerReimbursementRowProps> = ({
  reimbursement,
  selected,
  onSelect,
}) => {
  const [{ token }] = useAuth();
  const queryClient = useQueryClient();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [note, setNote] = useState(reimbursement.attributes.note || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStatusChange = async (newStatus: ReimbursementStatus) => {
    try {
      await updateReimbursement({
        id: reimbursement.id,
        data: {
          status: newStatus,
        },
        token,
      });
      queryClient.invalidateQueries('speaker-reimbursements');
      toast.success('Stato aggiornato con successo');
    } catch (error) {
      toast.error("Errore durante l'aggiornamento dello stato");
    }
  };

  const handleNoteSubmit = async () => {
    setIsSubmitting(true);
    try {
      await updateReimbursement({
        id: reimbursement.id,
        data: {
          note,
        },
        token,
      });
      queryClient.invalidateQueries('speaker-reimbursements');
      toast.success('Nota aggiornata con successo');
      setIsNoteModalOpen(false);
    } catch (error) {
      toast.error("Errore durante l'aggiornamento della nota");
    }
    setIsSubmitting(false);
  };

  const reimbursementModule = reimbursement.attributes
    .module as EventReimbursementModule;

  const updateMutation = useMutation(
    (params: { id: number; data: FormData | object }) =>
      updateAdminEventReimbursement(params.id, params.data, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('speaker-reimbursements');
        setIsEditModalOpen(false);
      },
    },
  );

  const handleEdit = async (data: ReimbursementAttributes, files: File[]) => {
    const formData = prepareReimbursementFormData(data, files);
    await updateMutation.mutateAsync({
      id: reimbursement.id,
      data: formData,
    });
  };

  const renderModuleContent = () => {
    if (!reimbursementModule) return null;

    const expensesTotal =
      (reimbursementModule.refund?.expenses?.length ?? 0) > 0
        ? (reimbursementModule.refund?.expenses?.reduce(
            (sum, expense) => sum + (expense.amount || 0),
            0,
          ) ?? 0)
        : (reimbursement.attributes.totalReimbursement ?? 0);

    const paymentAmount = reimbursementModule.payment?.amount || 0;

    const teachersExpensesTotal =
      reimbursementModule.refund?.expenses?.reduce(
        (sum, expense) =>
          expense.recipient === 'Docente' ? sum + (expense.amount || 0) : sum,
        0,
      ) ?? 0;

    const onavExpensesTotal =
      reimbursementModule.refund?.expenses?.reduce(
        (sum, expense) =>
          expense.recipient === 'ONAV' ? sum + (expense.amount || 0) : sum,
        0,
      ) ?? 0;

    const eventBalance =
      reimbursement.attributes.event?.data?.attributes?.balance;

    const isReimbursementPaid = () => {
      if (reimbursementModule.simplifiedPaymentDate) return true;
      if (!eventBalance) return false;

      const hasPaymentAmount =
        !reimbursementModule.payment?.waivePayment &&
        (reimbursementModule.payment?.amount ?? 0) > 0;
      const hasRefundAmount =
        !reimbursementModule.refund?.waiveRefund && expensesTotal > 0;

      if (!hasPaymentAmount && !hasRefundAmount) return false;

      const paymentPaid =
        !hasPaymentAmount || !!eventBalance.teacherPayments?.paymentDate;
      const refundPaid =
        !hasRefundAmount || !!eventBalance.teacherRefunds?.paymentDate;

      return paymentPaid && refundPaid;
    };
    const isPaid = isReimbursementPaid();

    return (
      <div className='space-y-8'>
        <div>
          <div className='flex flex-row justify-between items-center'>
            <h4 className='text-lg font-medium text-gray-900 mb-4'>
              Dettagli Evento
            </h4>
            {isPaid && (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                Pagato
              </span>
            )}
            {!isPaid &&
              reimbursement.attributes.status ===
                ReimbursementStatus.WAITING_COMPLETION && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                  Rendicontazione da compilare
                </span>
              )}
            {!isPaid &&
              (reimbursement.attributes.status ===
                ReimbursementStatus.IN_REVIEW ||
                reimbursement.attributes.status === null) && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800'>
                  Rendicontazione in attesa di approvazione
                </span>
              )}
            {!isPaid &&
              reimbursement.attributes.status ===
                ReimbursementStatus.APPROVED && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                  Rendicontazione approvata
                </span>
              )}
            {!isPaid &&
              reimbursement.attributes.status ===
                ReimbursementStatus.REJECTED && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                  Rendicontazione con documenti mancanti
                </span>
              )}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
            <div>
              <span className='text-sm text-gray-500'>Titolo Evento</span>
              <p className='text-sm text-gray-900'>
                {reimbursement.attributes.event?.data?.attributes?.title ??
                  reimbursementModule.description}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Data</span>
              <p className='text-sm text-gray-900'>
                {formatDate(
                  reimbursement.attributes.event?.data?.attributes?.startDate,
                  'LL • HH:mm',
                )}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Sezione</span>
              <p className='text-sm text-gray-900'>
                {reimbursement.attributes.event?.data?.attributes?.section?.data
                  ?.attributes?.name || ''}
              </p>
            </div>
          </div>

          <h4 className='text-lg font-medium text-gray-900 mb-4'>
            Dati Personali
          </h4>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-500'>Nome e Cognome</span>
              <p className='text-sm text-gray-900'>{`${reimbursementModule.personalInfo?.firstName} ${reimbursementModule.personalInfo?.lastName}`}</p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Codice Fiscale</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.fiscalCode}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Indirizzo</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.address}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Civico</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.streetNumber}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Città</span>
              <p className='text-sm text-gray-900'>{`${reimbursementModule.personalInfo?.city} (${reimbursementModule.personalInfo?.province}) ${reimbursementModule.personalInfo?.zip}`}</p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Email</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.email}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Telefono</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.phone}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>IBAN</span>
              <p className='text-sm text-gray-900'>
                {reimbursementModule.personalInfo?.iban}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Compenso</h4>
          {reimbursementModule.payment?.waivePayment ? (
            <p className='text-sm text-gray-500'>
              Il relatore ha rinunciato al compenso
            </p>
          ) : (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <span className='text-sm text-gray-500'>Importo</span>
                  <p className='text-sm text-gray-900'>
                    {formatCurrency(reimbursementModule.payment?.amount || 0)}
                  </p>
                </div>
                <div>
                  <span className='text-sm text-gray-500'>Evento</span>
                  <p className='text-sm text-gray-900'>
                    {reimbursementModule.payment?.eventName ??
                      reimbursementModule.description}
                  </p>
                </div>
                <div>
                  <span className='text-sm text-gray-500'>Data Evento</span>
                  <p className='text-sm text-gray-900'>
                    {formatDate(
                      reimbursementModule.payment?.eventDate || '',
                      'L',
                    )}
                  </p>
                </div>
                <div>
                  <span className='text-sm text-gray-500'>Sezione</span>
                  <p className='text-sm text-gray-900'>
                    {reimbursementModule.payment?.organizingSection}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Spese</h4>
          {reimbursementModule.refund?.waiveRefund ? (
            <p className='text-sm text-gray-500'>
              Il relatore ha rinunciato al rimborso spese
            </p>
          ) : (
            <div className='space-y-4'>
              {reimbursementModule.refund?.expenses?.map((expense, index) => (
                <div key={index} className='bg-gray-50 p-4 rounded-lg'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <span className='text-sm text-gray-500'>Tipo</span>
                      <p className='text-sm text-gray-900'>{expense.type}</p>
                    </div>
                    <div>
                      <span className='text-sm text-gray-500'>Data</span>
                      <p className='text-sm text-gray-900'>
                        {formatDate(expense.startDate, 'L')}
                        {expense.endDate &&
                          ` - ${formatDate(expense.endDate, 'L')}`}
                      </p>
                    </div>
                    <div>
                      <span className='text-sm text-gray-500'>Descrizione</span>
                      <p className='text-sm text-gray-900'>
                        {expense.description}
                      </p>
                    </div>
                    <div>
                      <span className='text-sm text-gray-500'>Importo</span>
                      <p className='text-sm text-gray-900'>
                        {formatCurrency(expense.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='bg-white shadow overflow-hidden sm:rounded-lg p-6'>
          <h4 className='text-lg font-IBM text-gray-600 mb-6'>
            Riepilogo Totali
          </h4>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            <div>
              <span className='text-sm text-gray-500'>
                TOTALE LORDO COMPENSO
              </span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(paymentAmount)}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>
                TOTALE LORDO RIMBORSO
              </span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(expensesTotal)}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>TOTALE LORDO</span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(paymentAmount + expensesTotal)}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>RITENUTA D'ACCONTO</span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(
                  reimbursementModule.payment?.waivePayment
                    ? 0
                    : (paymentAmount +
                        (reimbursementModule.refund?.waiveRefund
                          ? 0
                          : teachersExpensesTotal)) *
                        0.2,
                )}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>TOTALE NETTO</span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(
                  reimbursementModule.payment?.waivePayment
                    ? reimbursementModule.refund?.waiveRefund
                      ? 0
                      : expensesTotal
                    : (paymentAmount +
                        (reimbursementModule.refund?.waiveRefund
                          ? 0
                          : teachersExpensesTotal)) *
                        0.8,
                )}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>
                FATTURE INTESTATE A ONAV
              </span>
              <p className='text-sm font-medium text-gray-900'>
                {formatCurrency(onavExpensesTotal)}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Firma</h4>
          <p className='text-sm text-gray-900'>
            {reimbursementModule.signature}
          </p>
        </div>
      </div>
    );
  };

  const profile = reimbursement.attributes.profile?.data?.attributes;
  const { name, surname } = profile
    ? profile
    : {
        name: reimbursement.attributes.module?.personalInfo?.firstName,
        surname: reimbursement.attributes.module?.personalInfo?.lastName,
      };

  const mainEventBalance =
    reimbursement.attributes.event?.data?.attributes?.balance;
  const mainBalancePaymentDate =
    mainEventBalance?.teacherPayments?.paymentDate ||
    mainEventBalance?.teacherRefunds?.paymentDate;
  const paymentDate =
    mainBalancePaymentDate || reimbursementModule?.simplifiedPaymentDate;

  const isMainReimbursementPaid = () => {
    if (reimbursementModule?.simplifiedPaymentDate) return true;
    if (!mainEventBalance) return false;

    const hasPaymentAmount =
      !reimbursementModule?.payment?.waivePayment &&
      (reimbursement.attributes.totalCompensation ?? 0) > 0;
    const hasRefundAmount =
      !reimbursementModule?.refund?.waiveRefund &&
      (reimbursement.attributes.totalReimbursement ?? 0) > 0;

    if (!hasPaymentAmount && !hasRefundAmount) return false;

    const paymentPaid =
      !hasPaymentAmount || !!mainEventBalance.teacherPayments?.paymentDate;
    const refundPaid =
      !hasRefundAmount || !!mainEventBalance.teacherRefunds?.paymentDate;

    return paymentPaid && refundPaid;
  };
  const mainIsPaid = isMainReimbursementPaid();

  return (
    <>
      <tr className={selected ? 'bg-sand-50' : 'bg-white'}>
        <td className='relative w-12 px-6 sm:w-16 sm:px-8'>
          {selected && (
            <div className='absolute inset-y-0 left-0 w-0.5 bg-primary' />
          )}
          <Checkbox
            value={reimbursement.id}
            checked={selected}
            onChange={(event) => onSelect(event.target.checked)}
          />
        </td>
        <td
          className={`whitespace-nowrap px-6 py-4 text-sm sticky left-0 ${
            selected ? 'bg-sand-50 text-primary' : 'bg-white text-gray-500'
          }`}
        >
          {`${surname} ${name}`}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {formatDate(
            getReimbursmentSubmissionDate(reimbursement.attributes),
            'L',
          )}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {formatDate(
            reimbursement.attributes.event?.data?.attributes?.startDate ||
              reimbursementModule?.payment?.eventDate ||
              '',
            'L',
          )}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {reimbursement.attributes.event?.data?.attributes?.section?.data
            ?.attributes?.name ||
            reimbursementModule?.payment?.organizingSection ||
            ''}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {reimbursement.attributes.event?.data?.attributes?.title ||
            reimbursementModule?.payment?.eventName ||
            reimbursementModule?.description ||
            ''}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {formatCurrency(reimbursement.attributes.totalCompensation)}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {formatCurrency(reimbursement.attributes.totalReimbursement)}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {mainIsPaid && paymentDate ? formatDate(paymentDate, 'L') : ''}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {mainIsPaid ? (
            <select
              disabled
              className='rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm bg-green-50 text-green-800'
              value='PAGATO'
            >
              <option value='PAGATO'>PAGATO</option>
            </select>
          ) : (
            <select
              value={reimbursement.attributes.status as unknown as string}
              onChange={(e) =>
                handleStatusChange(e.target.value as ReimbursementStatus)
              }
              className='rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm'
            >
              <option value={ReimbursementStatus.IN_REVIEW}>
                Da approvare
              </option>
              <option value={ReimbursementStatus.WAITING_COMPLETION}>
                Bozza
              </option>
              <option value={ReimbursementStatus.REJECTED}>
                Documentazione incompleta
              </option>
              <option value={ReimbursementStatus.APPROVED}>Approvato</option>
            </select>
          )}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {reimbursement.attributes.attachments?.data?.length && (
            <button
              onClick={() => setIsAttachmentsModalOpen(true)}
              className='inline-flex items-center gap-1 px-2 py-1 text-primary hover:text-primary-dark rounded-md hover:bg-gray-50'
            >
              <PaperClipIcon className='h-4 w-4' />
              <span>{reimbursement.attributes.attachments?.data?.length}</span>
            </button>
          )}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          <button
            onClick={() => setIsNoteModalOpen(true)}
            className='text-primary hover:text-primary-dark'
          >
            {reimbursement.attributes.note ? 'Modifica nota' : 'Aggiungi nota'}
          </button>
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {reimbursementModule && (
            <div className='flex items-center gap-2'>
              <button
                onClick={() => setIsModuleModalOpen(true)}
                className='inline-flex items-center gap-1 px-2 py-1 text-primary hover:text-primary-dark rounded-md hover:bg-gray-50'
              >
                <DocumentTextIcon className='h-4 w-4' />
                <span>Visualizza</span>
              </button>
              <button
                onClick={() => setIsEditModalOpen(true)}
                className='inline-flex items-center gap-1 px-2 py-1 text-primary hover:text-primary-dark rounded-md hover:bg-gray-50'
              >
                <PencilIcon className='h-4 w-4' />
                <span>Modifica</span>
              </button>
              <PDFDownloadLink
                document={
                  <SpeakerReimbursementPDF reimbursement={reimbursement} />
                }
                fileName={`rimborso-relatore-${reimbursement.id}.pdf`}
                className='inline-flex items-center gap-1 px-2 py-1 text-primary hover:text-primary-dark rounded-md hover:bg-gray-50'
              >
                {({ loading }) =>
                  loading ? (
                    <span>Caricamento...</span>
                  ) : (
                    <>
                      <DocumentDownloadIcon className='h-4 w-4' />
                      <span>PDF</span>
                    </>
                  )
                }
              </PDFDownloadLink>
            </div>
          )}
        </td>
        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
          {reimbursement.id}
        </td>
      </tr>

      <BaseModal
        isOpen={isNoteModalOpen}
        toggle={() => setIsNoteModalOpen(false)}
        title={
          reimbursement.attributes.note ? 'Modifica nota' : 'Aggiungi nota'
        }
        onConfirm={handleNoteSubmit}
        onCancel={() => setIsNoteModalOpen(false)}
        isLoading={isSubmitting}
      >
        <textarea
          rows={4}
          className='shadow-sm block w-full focus:ring-primary focus:border-primary sm:text-sm border border-gray-300 rounded-md'
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </BaseModal>

      <BaseModal
        isOpen={isAttachmentsModalOpen}
        toggle={() => setIsAttachmentsModalOpen(false)}
        title='Lista allegati'
        hideBottom
      >
        <div className='space-y-4'>
          {reimbursement.attributes.attachments?.data?.map(
            (attachment, index) => {
              const strapiBaseUrl =
                process.env.REACT_APP_API_URL?.replace('/api', '') || '';
              const attachmentUrl = `${strapiBaseUrl}${attachment.attributes.url}`;
              return (
                <div key={index} className='flex items-center justify-between'>
                  <span className='text-sm text-gray-600'>
                    {attachment.attributes.name}
                  </span>
                  <div className='flex items-center gap-4'>
                    <a
                      href={attachmentUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(attachmentUrl)
                          .then((response) => response.blob())
                          .then((blob) => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute(
                              'download',
                              attachment.attributes.name,
                            );
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          })
                          .catch((error) =>
                            console.error('Download failed:', error),
                          );
                      }}
                      className='text-primary hover:text-primary-dark text-sm font-medium'
                    >
                      Scarica
                    </a>
                    <a
                      href={attachmentUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:text-primary-dark text-sm font-medium'
                    >
                      Apri
                    </a>
                  </div>
                </div>
              );
            },
          )}
        </div>
      </BaseModal>

      <BaseModal
        isOpen={isModuleModalOpen}
        toggle={() => setIsModuleModalOpen(false)}
        title='Dettagli Modulo'
        hideBottom
      >
        <div className='max-h-[80vh] overflow-y-auto px-4'>
          {renderModuleContent()}
        </div>
      </BaseModal>

      <AdminEventReimbursementFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEdit}
        key={isEditModalOpen ? 'open' : 'closed'}
        initialData={{
          ...reimbursement.attributes,
          event: reimbursement.attributes.event?.data?.id ?? null,
          profile: reimbursement.attributes.profile?.data?.id ?? null,
          lesson: null,
          submittedAt: getReimbursmentSubmissionDate(reimbursement.attributes),
          module: {
            ...reimbursement.attributes.module,
            payment: {
              ...reimbursementModule?.payment,
              eventName:
                reimbursementModule?.payment?.eventName ||
                reimbursement.attributes.event?.data?.attributes?.title ||
                '',
              eventDate:
                reimbursementModule?.payment?.eventDate ||
                reimbursement.attributes.event?.data?.attributes?.startDate?.split(
                  'T',
                )[0] ||
                '',
              organizingSection:
                reimbursementModule?.payment?.organizingSection ||
                reimbursement.attributes.event?.data?.attributes?.section?.data
                  ?.attributes?.name ||
                '',
            },
          },
        }}
        isEdit
      />
    </>
  );
};

export default SpeakerReimbursementRow;
